import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import TopUpSheet from '../components/TopUpSheet';
import { useUser } from '../contexts/UserContext';
import type { CreditPack } from '../types/api';
import {
  CREDIT_COPY,
  blockFor,
  isPaidCreditsOnly,
  shortfallFor,
  spendableFor,
  type CreditBlock,
} from '../utils/creditRule';

/** How long the green "unlocked" line stays up after a purchase. */
const UNLOCKED_MS = 3000;
/** Let the green light land before the send takes over. */
const UNLOCK_PAUSE_MS = 700;

export interface CreditGate {
  /** Why the scan can't run right now ('none' when it can). */
  block: CreditBlock;
  /** Credits that can pay for this feature (purchased only, for Free on a paid-only feature). */
  spendable: number;
  /**
   * The send-time gate. Resolves true when the scan can go. Otherwise opens
   * the top-up sheet worded for the reason, with the covering pack
   * pre-selected; resolves true after a purchase that unlocks the scan (the
   * green "unlocked" line shows first), false when dismissed or still pending.
   */
  ensure: () => Promise<boolean>;
  /** Low-key availability line for paid-credits-only features, else null. */
  note: React.ReactNode;
  /** The green "Unlocked · sending your scan" line right after a purchase. */
  unlockedLine: React.ReactNode;
  /** The sheet; render it once anywhere in the screen. */
  sheet: React.ReactNode;
}

/**
 * The credit gate for a metered scan screen (SCRUM-196). Photo picking and
 * the face check are never blocked by credits; only sending is.
 */
export function useCreditGate(featureKey: string): CreditGate {
  const { credits } = useUser();
  const [open, setOpen] = useState(false);
  const [unlockedAt, setUnlockedAt] = useState<number | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  const block = blockFor(credits, featureKey);
  const settle = useCallback((ok: boolean) => {
    const r = resolver.current;
    resolver.current = null;
    setOpen(false);
    r?.(ok);
  }, []);

  const ensure = useCallback((): Promise<boolean> => {
    if (blockFor(credits, featureKey) === 'none') return Promise.resolve(true);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
      setOpen(true);
    });
  }, [credits, featureKey]);

  const onPurchased = useCallback(
    (pack: CreditPack) => {
      // The balance refresh lands asynchronously; decide from the known
      // balance plus the pack (purchased credits always go to surplus).
      const after = credits
        ? {
            ...credits,
            surplus_balance: credits.surplus_balance + pack.credits,
            total: credits.total + pack.credits,
          }
        : credits;
      if (blockFor(after, featureKey) !== 'none') {
        settle(false);
        return;
      }
      setUnlockedAt(Date.now());
      const r = resolver.current;
      resolver.current = null;
      setOpen(false);
      window.setTimeout(() => r?.(true), UNLOCK_PAUSE_MS);
    },
    [credits, featureKey, settle],
  );

  useEffect(() => {
    if (unlockedAt == null) return;
    const id = window.setTimeout(() => { setUnlockedAt(null); }, UNLOCKED_MS);
    return () => { window.clearTimeout(id); };
  }, [unlockedAt]);

  // Never leave a caller awaiting a sheet that unmounted with the screen.
  useEffect(() => () => { resolver.current?.(false); }, []);

  const paidOnly = isPaidCreditsOnly(credits, featureKey);
  const note = paidOnly ? (
    <p className="t-small t-mute" data-testid="paid-credits-note" style={{ margin: '6px 0 0', fontSize: 12 }}>
      {CREDIT_COPY.paidOnlyNote}
    </p>
  ) : null;

  const unlockedLine = (
    <AnimatePresence>
      {unlockedAt != null && (
        <motion.p
          key="unlocked"
          role="status"
          className="t-small"
          style={{ margin: '6px 0 0', color: 'var(--c-deep-teal)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ type: 'spring', stiffness: 520, damping: 26 }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="11" fill="var(--c-kivo-teal)" />
            <path d="M7 12.5l3.2 3.2L17 9" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {CREDIT_COPY.unlockedSending}
        </motion.p>
      )}
    </AnimatePresence>
  );

  const paidRequired = block === 'paid_credits_required';
  const sheet = (
    <AnimatePresence>
      {open && (
        <TopUpSheet
          key={`credit-gate-${featureKey}`}
          title={paidRequired ? CREDIT_COPY.unlockTitle : null}
          message={paidRequired ? CREDIT_COPY.unlockBody : CREDIT_COPY.topUpBody}
          minCredits={shortfallFor(credits, featureKey)}
          onClose={() => { settle(false); }}
          onPurchased={onPurchased}
        />
      )}
    </AnimatePresence>
  );

  return { block, spendable: spendableFor(credits, featureKey), ensure, note, unlockedLine, sheet };
}
